import { geometryItemState, prepareFrameBoundsChunks, validateBillboardDirection } from './_pure/frameBounds.js';
import { deepValueEqual } from './_pure/valueEquality.js';
import { createRecordReadLog, trackRecordReads } from './recordReadTracking.js';
/** Reuses exact renderer-derived chunks, retaining only the latest frame's constellations. Inputs are immutable. */
export function createFrameExtentPointCollector() {
    let entries = new Map();
    let previousChunks = [];
    let previousPoints = [];
    return (frame, options) => {
        validateBillboardDirection(options?.billboardDirection);
        const nextEntries = new Map();
        const chunks = [];
        const topologyEquality = new Map();
        let geometryContext;
        const points = [];
        for (const { key, input, context, collect } of prepareFrameBoundsChunks(frame, true, options?.billboardDirection)) {
            const contextInput = () => geometryContext ??= {
                ...context,
                itemParametricStateByKey: Object.fromEntries(Object.entries(context.itemParametricStateByKey ?? {})
                    .map(([itemKey, state]) => [itemKey, geometryItemState(state)])),
                entityConstellationParametricStateByKey: context.entityConstellationParametricStateByKey ?? {},
            };
            const readsUnchanged = (log, previous, next) => {
                if (log.enumerated)
                    return deepValueEqual(previous, next);
                for (const [property, value] of log.reads) {
                    if (!deepValueEqual(value, Reflect.get(next, property)))
                        return false;
                }
                return true;
            };
            const sameDependencies = (dependencies) => {
                if (dependencies.topology) {
                    let equal = topologyEquality.get(dependencies.topology);
                    if (equal === undefined) {
                        equal = deepValueEqual(dependencies.topology, context.entityConstellations);
                        topologyEquality.set(dependencies.topology, equal);
                    }
                    if (!equal)
                        return false;
                }
                if (dependencies.allCollections && !deepValueEqual([...dependencies.allCollections], [...context.entityItemsByCollectionKey]))
                    return false;
                for (const [collectionKey, items] of dependencies.collections) {
                    if (!deepValueEqual(items, context.entityItemsByCollectionKey.get(collectionKey)))
                        return false;
                }
                const geometry = contextInput();
                return readsUnchanged(dependencies.itemReads, dependencies.enumeratedItems, geometry.itemParametricStateByKey)
                    && readsUnchanged(dependencies.constellationReads, dependencies.enumeratedConstellations, geometry.entityConstellationParametricStateByKey);
            };
            const previous = entries.get(key);
            let entry;
            if (previous && deepValueEqual(previous.input, input)
                && sameDependencies(previous.dependencies)) {
                entry = previous;
            }
            else {
                const geometry = contextInput();
                const dependencies = {
                    collections: new Map(),
                    itemReads: createRecordReadLog(), constellationReads: createRecordReadLog(),
                };
                const trackedCollections = new Proxy(geometry.entityItemsByCollectionKey, {
                    get(target, property) {
                        if (property === 'get')
                            return (collectionKey) => {
                                const items = target.get(collectionKey);
                                dependencies.collections.set(collectionKey, items);
                                return items;
                            };
                        dependencies.allCollections = geometry.entityItemsByCollectionKey;
                        const value = Reflect.get(target, property, target);
                        return typeof value === 'function' ? value.bind(target) : value;
                    },
                });
                const trackedContext = {
                    get entityConstellations() {
                        dependencies.topology = geometry.entityConstellations;
                        return geometry.entityConstellations;
                    },
                    entityItemsByCollectionKey: trackedCollections,
                    itemParametricStateByKey: trackRecordReads(geometry.itemParametricStateByKey, dependencies.itemReads),
                    entityConstellationParametricStateByKey: trackRecordReads(geometry.entityConstellationParametricStateByKey, dependencies.constellationReads),
                };
                const chunk = collect(trackedContext).points;
                if (dependencies.itemReads.enumerated)
                    dependencies.enumeratedItems = geometry.itemParametricStateByKey;
                if (dependencies.constellationReads.enumerated) {
                    dependencies.enumeratedConstellations = geometry.entityConstellationParametricStateByKey;
                }
                entry = { input, dependencies, points: chunk };
            }
            nextEntries.set(key, entry);
            chunks.push(entry.points);
            points.push(...entry.points);
        }
        entries = nextEntries;
        if (chunks.length !== previousChunks.length
            || chunks.some((chunk, index) => chunk !== previousChunks[index])) {
            previousPoints = points;
        }
        previousChunks = chunks;
        return previousPoints;
    };
}
